/**
 * QuestionsModule — Chat clients (admin).
 *
 * Batch #14.157 — Refonte UX :
 *  - Tuile « Chat clients » du DocumentsLauncher pointe désormais directement ici
 *    (plus de tuile-dans-tuile via /admin/messages).
 *  - Bouton « Nouvelle conversation » TOUJOURS visible (avec picker client intégré).
 *  - Recherche plein-texte sur sujet + dernier message.
 *  - KPIs en bandeau : Non lus · Attente client · Urgents.
 *  - Auto-scroll au bas des messages à l'ouverture d'une conversation.
 *  - Indicateur visuel non lu (point bleu sur les conversations avec messages
 *    récents côté client).
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { api } from "@/api";
import { useClient } from "@/contexts/ClientContext";
import { toast } from "@/lib/toast";
import { FISCAL_YEARS } from "@/constants/modules";
import {
  MessageCircle, Plus, Send, X, User, Building2, Trash2, Users,
  Filter, Flag, BookOpen, Search, AlertCircle, Clock,
} from "lucide-react";
import RelanceButton from "@/components/shared/RelanceButton";
import ArchiveButton from "@/components/admin/ArchiveButton";

const STATUS_MAP = {
  ouvert: { label: "Ouvert", bg: "bg-blue-100", text: "text-blue-700" },
  attente_client: { label: "Attente client", bg: "bg-amber-100", text: "text-amber-700" },
  attente_cabinet: { label: "Attente cabinet", bg: "bg-orange-100", text: "text-orange-700" },
  repondu: { label: "Répondu", bg: "bg-green-100", text: "text-green-700" },
  a_clarifier: { label: "À clarifier", bg: "bg-purple-100", text: "text-purple-700" },
  ferme: { label: "Fermé", bg: "bg-gray-100", text: "text-gray-500" },
};

const PRIORITY_MAP = {
  haute: { label: "Haute", color: "bg-red-100 text-red-700" },
  normale: { label: "Normale", color: "bg-blue-100 text-blue-700" },
  basse: { label: "Basse", color: "bg-gray-100 text-gray-500" },
};

export default function QuestionsModule() {
  const { clients, fetchClients } = useClient();
  const allClients = clients || [];

  useEffect(() => {
    if (!clients || clients.length === 0) fetchClients();
  }, [clients, fetchClients]);

  const [localClientId, setLocalClientId] = useState("");
  const [questions, setQuestions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showTemplatesPicker, setShowTemplatesPicker] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newClientId, setNewClientId] = useState("");
  const [newSubject, setNewSubject] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [newPriority, setNewPriority] = useState("normale");
  const [newYear, setNewYear] = useState(FISCAL_YEARS[0]);
  const [selectedQ, setSelectedQ] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [search, setSearch] = useState("");
  const messagesEndRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (localClientId) params.append("client_id", localClientId);
      if (filterStatus) params.append("status", filterStatus);
      if (filterPriority) params.append("priority", filterPriority);
      if (filterYear) params.append("tax_year", filterYear);
      const res = await api.get(`/questions?${params}`);
      setQuestions(res.data);
    } catch { /* silent */ }
  }, [localClientId, filterStatus, filterPriority, filterYear]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTemplates(); }, []);

  useEffect(() => {
    if (selectedQ) {
      const fresh = questions.find(q => q.id === selectedQ.id);
      if (fresh) setSelectedQ(fresh);
    }
  }, [questions, selectedQ]);

  // Batch #14.180 — Marque les messages CLIENT comme LUS dès qu'on ouvre une conversation
  useEffect(() => {
    if (!selectedQ?.id) return;
    const hasUnreadFromClient = (selectedQ.messages || []).some(
      (m) => m.author === "client" && !m.read_at,
    );
    if (!hasUnreadFromClient) return;
    api.post(`/questions/${selectedQ.id}/mark-read`)
      .then(() => { load?.(); })
      .catch(() => { /* silent, best-effort */ });
  }, [selectedQ?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll au bas des messages quand on ouvre une conversation ou qu'un nouveau message arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [selectedQ?.id, selectedQ?.messages?.length]);

  // Batch #14.181 — Typing indicator : poll toutes les 3s + heartbeat sur keystroke
  const [clientTyping, setClientTyping] = useState(false);
  const lastTypingPostRef = useRef(0);

  useEffect(() => {
    if (!selectedQ?.id) {
      setClientTyping(false);
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get("/questions");
        if (cancelled) return;
        const fresh = (r.data || []).find((q) => q.id === selectedQ.id);
        const until = fresh?.typing_client_until;
        if (until) {
          const ms = new Date(until).getTime() - Date.now();
          setClientTyping(ms > 0);
        } else {
          setClientTyping(false);
        }
      } catch { /* silent */ }
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [selectedQ?.id]);

  const notifyTyping = useCallback(() => {
    if (!selectedQ?.id) return;
    const now = Date.now();
    if (now - lastTypingPostRef.current < 2500) return;  // throttle 1 POST / 2.5s
    lastTypingPostRef.current = now;
    api.post(`/questions/${selectedQ.id}/typing`).catch(() => { /* silent */ });
  }, [selectedQ?.id]);

  const loadTemplates = async () => {
    try { const r = await api.get("/question-templates"); setTemplates(r.data || []); }
    catch { /* silent */ }
  };

  const create = async () => {
    if (!newClientId || !newSubject.trim() || !newMessage.trim()) return;
    setLoading(true);
    try {
      const res = await api.post("/questions", {
        client_id: newClientId, tax_year: newYear, subject: newSubject,
        message: newMessage, priority: newPriority,
      });
      toast.success("Conversation envoyée");
      setShowNew(false); setNewSubject(""); setNewMessage(""); setNewPriority("normale");
      setSelectedQ(res.data);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  const reply = async () => {
    if (!selectedQ || !replyText.trim()) return;
    setLoading(true);
    try {
      const res = await api.post(`/questions/${selectedQ.id}/reply`, { message: replyText });
      setSelectedQ(res.data);
      setReplyText("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  const applyTemplate = (tmpl) => {
    setReplyText(tmpl.body);
    setShowTemplatesPicker(false);
  };

  const changeStatus = async (qId, status) => {
    try {
      await api.put(`/questions/${qId}/status`, { status });
      toast.success("Statut mis à jour");
      if (selectedQ?.id === qId) setSelectedQ(prev => ({ ...prev, status }));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const changePriority = async (qId, priority) => {
    try {
      await api.put(`/questions/${qId}/priority`, { priority });
      toast.success("Priorité mise à jour");
      if (selectedQ?.id === qId) setSelectedQ(prev => ({ ...prev, priority }));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const deleteQ = async (qId) => {
    if (!window.confirm("Supprimer cette conversation ?")) return;
    try {
      await api.delete(`/questions/${qId}`);
      if (selectedQ?.id === qId) setSelectedQ(null);
      toast.success("Supprimé");
      load();
    } catch { toast.error("Erreur"); }
  };

  const clientName = (q) => q.client_name || allClients.find(c => c.id === q.client_id)?.first_name || q.client_id;

  // Filtrage local pour la recherche plein-texte
  const filteredQuestions = useMemo(() => {
    if (!search.trim()) return questions;
    const s = search.toLowerCase();
    return questions.filter((q) => {
      if ((q.subject || "").toLowerCase().includes(s)) return true;
      if (clientName(q).toLowerCase().includes(s)) return true;
      const lastMsg = q.messages?.[q.messages.length - 1];
      if (lastMsg?.message?.toLowerCase().includes(s)) return true;
      return false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, search, allClients]);

  // KPIs basés sur la liste complète (non filtrée par la recherche)
  const stats = useMemo(() => {
    let unread = 0, awaitingCabinet = 0, urgent = 0;
    for (const q of questions) {
      const last = q.messages?.[q.messages.length - 1];
      if (last && last.author !== "cabinet") unread += 1;
      if (q.status === "attente_cabinet" || q.status === "ouvert") awaitingCabinet += 1;
      if (q.priority === "haute") urgent += 1;
    }
    return { unread, awaitingCabinet, urgent };
  }, [questions]);

  const isUnread = (q) => {
    const last = q.messages?.[q.messages.length - 1];
    return last && last.author !== "cabinet";
  };

  return (
    <div className="space-y-4" data-testid="questions-module">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-indigo-600" /> Chat clients
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">Messagerie bidirectionnelle avec vos clients</p>
        </div>
        <button
          onClick={() => {
            setNewClientId(localClientId || "");
            setShowNew(true);
          }}
          className="bg-indigo-600 text-white px-3.5 py-2 rounded-lg text-sm flex items-center gap-1.5 hover:bg-indigo-700 shadow-sm transition active:scale-95"
          data-testid="qa-new-btn"
        >
          <Plus className="w-4 h-4" /> Nouvelle conversation
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3" data-testid="qa-kpis">
        <div className={`rounded-xl border p-3 ${stats.unread > 0 ? "bg-blue-50 border-blue-200" : "bg-white border-slate-200"}`}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Non lus</span>
            <MessageCircle className={`w-4 h-4 ${stats.unread > 0 ? "text-blue-600" : "text-slate-300"}`} />
          </div>
          <div className={`text-2xl font-bold mt-1 ${stats.unread > 0 ? "text-blue-700" : "text-slate-400"}`} data-testid="qa-kpi-unread">
            {stats.unread}
          </div>
        </div>
        <div className={`rounded-xl border p-3 ${stats.awaitingCabinet > 0 ? "bg-orange-50 border-orange-200" : "bg-white border-slate-200"}`}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Attente cabinet</span>
            <Clock className={`w-4 h-4 ${stats.awaitingCabinet > 0 ? "text-orange-600" : "text-slate-300"}`} />
          </div>
          <div className={`text-2xl font-bold mt-1 ${stats.awaitingCabinet > 0 ? "text-orange-700" : "text-slate-400"}`} data-testid="qa-kpi-awaiting">
            {stats.awaitingCabinet}
          </div>
        </div>
        <div className={`rounded-xl border p-3 ${stats.urgent > 0 ? "bg-rose-50 border-rose-200" : "bg-white border-slate-200"}`}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Urgents</span>
            <AlertCircle className={`w-4 h-4 ${stats.urgent > 0 ? "text-rose-600" : "text-slate-300"}`} />
          </div>
          <div className={`text-2xl font-bold mt-1 ${stats.urgent > 0 ? "text-rose-700" : "text-slate-400"}`} data-testid="qa-kpi-urgent">
            {stats.urgent}
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white border rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="qa-filters">
        <div className="flex items-center gap-1.5 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher dans les conversations..."
            className="flex-1 border rounded-lg px-2 py-1.5 text-xs"
            data-testid="qa-search"
          />
        </div>
        <Filter className="w-4 h-4 text-gray-300" />
        <div className="flex items-center gap-1.5">
          <Users className="w-4 h-4 text-gray-400" />
          <select value={localClientId}
            onChange={e => { setLocalClientId(e.target.value); setSelectedQ(null); }}
            className="border rounded-lg px-2 py-1.5 text-xs min-w-[180px]"
            data-testid="qa-client-select">
            <option value="">Tous les clients</option>
            {allClients.map(c => (
              <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
            ))}
          </select>
        </div>
        <select value={filterYear} onChange={e => setFilterYear(e.target.value)}
          className="border rounded-lg px-2 py-1.5 text-xs" data-testid="qa-filter-year">
          <option value="">Toutes années</option>
          {FISCAL_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border rounded-lg px-2 py-1.5 text-xs" data-testid="qa-filter-status">
          <option value="">Tous les statuts</option>
          {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
          className="border rounded-lg px-2 py-1.5 text-xs" data-testid="qa-filter-priority">
          <option value="">Toutes priorités</option>
          {Object.entries(PRIORITY_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        {(filterStatus || filterPriority || filterYear || search) && (
          <button
            onClick={() => { setFilterStatus(""); setFilterPriority(""); setFilterYear(""); setSearch(""); }}
            className="text-xs text-indigo-600 hover:underline"
            data-testid="qa-reset-filters"
          >
            Réinitialiser
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Liste des conversations */}
        <div className="lg:col-span-2 space-y-2 max-h-[70vh] overflow-y-auto" data-testid="qa-list">
          {!filteredQuestions.length && (
            <div className="bg-white border rounded-xl p-8 text-center text-gray-400">
              <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">
                {search ? "Aucun résultat pour cette recherche" :
                 localClientId ? "Aucune conversation pour ce client" : "Aucune conversation"}
              </p>
              {!search && !localClientId && (
                <button
                  onClick={() => { setNewClientId(""); setShowNew(true); }}
                  className="mt-3 text-xs text-indigo-600 hover:underline inline-flex items-center gap-1"
                  data-testid="qa-empty-cta"
                >
                  <Plus className="w-3 h-3" /> Démarrer une nouvelle conversation
                </button>
              )}
            </div>
          )}
          {filteredQuestions.map(q => {
            const st = STATUS_MAP[q.status] || STATUS_MAP.ouvert;
            const pr = PRIORITY_MAP[q.priority] || PRIORITY_MAP.normale;
            const active = selectedQ?.id === q.id;
            const lastMsg = q.messages?.[q.messages.length - 1];
            const unread = isUnread(q);
            return (
              <button key={q.id} onClick={() => setSelectedQ(q)}
                className={`w-full text-left bg-white border rounded-xl p-3 transition hover:shadow-sm relative ${active ? "ring-2 ring-indigo-400 border-indigo-300" : ""}`}
                data-testid={`qa-item-${q.id}`}>
                {unread && (
                  <span className="absolute top-3 left-1 w-2 h-2 rounded-full bg-blue-500 animate-pulse" title="Nouveau message non lu" />
                )}
                <div className={`flex items-start justify-between gap-2 ${unread ? "pl-3" : ""}`}>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm truncate ${unread ? "font-bold text-slate-900" : "font-semibold text-slate-700"}`}>{q.subject}</p>
                    <p className="text-xs text-gray-500">{clientName(q)} — {q.tax_year}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap font-medium ${st.bg} ${st.text}`}>
                      {st.label}
                    </span>
                    {q.priority === "haute" && (
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${pr.color} flex items-center gap-0.5`}>
                        <Flag className="w-2.5 h-2.5" /> {pr.label}
                      </span>
                    )}
                  </div>
                </div>
                {lastMsg && (
                  <p className={`text-xs mt-1 truncate ${unread ? "text-slate-700 font-medium" : "text-gray-400"} ${unread ? "pl-3" : ""}`}>
                    <span className={lastMsg.author === "cabinet" ? "text-indigo-500" : "text-blue-500"}>
                      {lastMsg.author === "cabinet" ? "Vous" : "Client"}
                    </span>: {lastMsg.message}
                  </p>
                )}
                <p className={`text-[10px] text-gray-300 mt-1 ${unread ? "pl-3" : ""}`}>
                  {new Date(q.updated_at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" })}
                </p>
              </button>
            );
          })}
        </div>

        {/* Conversation active */}
        <div className="lg:col-span-3">
          {selectedQ ? (
            <div className="bg-white border rounded-xl overflow-hidden flex flex-col"
              style={{ maxHeight: "70vh", minHeight: "50vh" }} data-testid="qa-conversation">
              {/* Header */}
              <div className="px-4 py-3 border-b bg-gradient-to-r from-indigo-50 to-white flex items-center justify-between flex-wrap gap-2">
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-sm truncate">{selectedQ.subject}</h3>
                  <p className="text-xs text-gray-500">{clientName(selectedQ)} — Année fiscale {selectedQ.tax_year}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <select value={selectedQ.priority || "normale"}
                    onChange={e => changePriority(selectedQ.id, e.target.value)}
                    className="border rounded-lg px-2 py-1 text-xs" data-testid="qa-priority-select">
                    {Object.entries(PRIORITY_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                  <select value={selectedQ.status}
                    onChange={e => changeStatus(selectedQ.id, e.target.value)}
                    className="border rounded-lg px-2 py-1 text-xs" data-testid="qa-status-select">
                    {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                  {selectedQ.client_id && ["ouvert", "attente_client"].includes(selectedQ.status) && (
                    <RelanceButton
                      clientId={selectedQ.client_id}
                      targetType="message"
                      targetId={selectedQ.id}
                      variant="small"
                    />
                  )}
                  <button onClick={() => deleteQ(selectedQ.id)}
                    className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-rose-50" data-testid="qa-delete-btn"
                    title="Supprimer la conversation">
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <ArchiveButton
                    itemType="question"
                    itemId={selectedQ.id}
                    itemLabel="cette question"
                    onArchived={() => { setSelectedQ(null); load(); }}
                  />
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/40" data-testid="qa-messages">
                {(() => {
                  const msgs = selectedQ.messages || [];
                  // Batch #14.180 — Trouve l'INDEX du dernier message envoyé par CABINET pour afficher "Lu"/"Distribué"
                  let lastOwnIdx = -1;
                  for (let i = msgs.length - 1; i >= 0; i--) {
                    if (msgs[i].author === "cabinet") { lastOwnIdx = i; break; }
                  }
                  return msgs.map((msg, idx) => {
                    const isCabinet = msg.author === "cabinet";
                    const showReceipt = isCabinet && idx === lastOwnIdx;
                    return (
                      <div key={msg.id} className={`flex flex-col ${isCabinet ? "items-end" : "items-start"}`}>
                        <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 shadow-sm ${isCabinet ? "bg-indigo-600 text-white rounded-br-sm" : "bg-white text-gray-800 border border-slate-200 rounded-bl-sm"}`}>
                          <div className="flex items-center gap-1.5 mb-1">
                            {isCabinet
                              ? <Building2 className="w-3 h-3 opacity-70" />
                              : <User className="w-3 h-3 text-gray-400" />}
                            <span className={`text-[10px] font-medium ${isCabinet ? "text-indigo-200" : "text-gray-500"}`}>
                              {msg.author_name}
                            </span>
                            <span className={`text-[10px] ${isCabinet ? "text-indigo-300" : "text-gray-400"}`}>
                              {new Date(msg.created_at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" })}
                            </span>
                          </div>
                          <p className="text-sm whitespace-pre-line">{msg.message}</p>
                        </div>
                        {showReceipt && (
                          <span className="text-[10px] text-gray-400 mt-0.5 mr-1" data-testid={`qa-receipt-${msg.id}`}>
                            {msg.read_at ? "Lu" : "Distribué"}
                          </span>
                        )}
                      </div>
                    );
                  });
                })()}
                {clientTyping && (
                  <div className="flex items-start" data-testid="qa-typing-indicator">
                    <div className="bg-white border border-slate-200 text-gray-500 rounded-2xl rounded-bl-sm px-3 py-2 inline-flex items-center gap-1.5 shadow-sm">
                      <span className="text-[10px] italic">Le client écrit</span>
                      <span className="flex gap-0.5">
                        <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                      </span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Zone de réponse */}
              {selectedQ.status !== "ferme" && (
                <div className="border-t bg-white" data-testid="qa-reply-area">
                  {showTemplatesPicker && (
                    <div className="p-2 bg-gray-50 border-b space-y-1 max-h-40 overflow-y-auto">
                      <p className="text-[10px] text-gray-500 uppercase px-2">Réponses rapides</p>
                      {templates.map(t => (
                        <button key={t.id} onClick={() => applyTemplate(t)}
                          className="w-full text-left px-2 py-1.5 text-xs hover:bg-indigo-50 rounded"
                          data-testid={`qa-template-${t.id}`}>
                          <span className="font-medium text-gray-700">{t.name}</span>
                          <p className="text-gray-500 truncate">{t.body}</p>
                        </button>
                      ))}
                      {templates.length === 0 && (
                        <p className="text-xs text-gray-400 px-2 py-1">Aucun modèle disponible</p>
                      )}
                    </div>
                  )}
                  <div className="p-3 flex gap-2">
                    <button onClick={() => setShowTemplatesPicker(!showTemplatesPicker)}
                      className="border px-2 py-2 rounded-lg hover:bg-gray-50 text-gray-500"
                      title="Réponses rapides" data-testid="qa-templates-toggle">
                      <BookOpen className="w-4 h-4" />
                    </button>
                    <textarea value={replyText}
                      onChange={e => { setReplyText(e.target.value); notifyTyping(); }}
                      onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); reply(); } }}
                      className="flex-1 border rounded-lg px-3 py-2 text-sm resize-none" rows={2}
                      placeholder="Écrire une réponse... (Entrée pour envoyer, Maj+Entrée pour saut de ligne)"
                      maxLength={5000} data-testid="qa-reply-input" />
                    <button onClick={reply} disabled={loading || !replyText.trim()}
                      className="bg-indigo-600 text-white px-3 py-2 rounded-lg text-sm disabled:opacity-50 self-stretch hover:bg-indigo-700"
                      data-testid="qa-reply-send">
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white border rounded-xl p-12 text-center text-gray-400" data-testid="qa-empty-conversation">
              <MessageCircle className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Sélectionnez une conversation</p>
              <p className="text-xs mt-1">— ou —</p>
              <button
                onClick={() => { setNewClientId(""); setShowNew(true); }}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700"
                data-testid="qa-empty-conv-cta"
              >
                <Plus className="w-3.5 h-3.5" /> Démarrer une conversation
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal Nouvelle conversation */}
      {showNew && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             onClick={() => setShowNew(false)} data-testid="qa-new-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[92vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="font-bold text-slate-800 inline-flex items-center gap-2">
                <Plus className="w-4 h-4 text-indigo-600" /> Nouvelle conversation
              </h3>
              <button onClick={() => setShowNew(false)} className="text-slate-400 hover:text-slate-700 p-1 rounded hover:bg-slate-100"
                      data-testid="qa-new-modal-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Client destinataire *</label>
                <select value={newClientId}
                        onChange={(e) => setNewClientId(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        data-testid="qa-new-client">
                  <option value="">— Choisir un client —</option>
                  {allClients.map((c) => (
                    <option key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.email ? `· ${c.email}` : ""}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Sujet *</label>
                  <input type="text" value={newSubject} onChange={e => setNewSubject(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="Ex : Relevé T4 manquant" data-testid="qa-new-subject" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Priorité</label>
                  <select value={newPriority} onChange={e => setNewPriority(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="qa-new-priority">
                    {Object.entries(PRIORITY_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Année fiscale</label>
                <select value={newYear} onChange={e => setNewYear(Number(e.target.value))}
                  className="w-full sm:w-auto border rounded-lg px-3 py-2 text-sm" data-testid="qa-new-year">
                  {FISCAL_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Message *</label>
                <textarea value={newMessage} onChange={e => setNewMessage(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm h-28 resize-none"
                  placeholder="Bonjour, …" data-testid="qa-new-message" maxLength={5000} />
                <p className="text-[10px] text-slate-400 mt-1 text-right">{newMessage.length} / 5000</p>
              </div>
            </div>
            <div className="px-5 py-3 border-t bg-slate-50 sticky bottom-0 flex justify-end gap-2">
              <button onClick={() => setShowNew(false)} className="px-3 py-1.5 rounded-lg border bg-white hover:bg-slate-50 text-sm">
                Annuler
              </button>
              <button onClick={create}
                disabled={loading || !newClientId || !newSubject.trim() || !newMessage.trim()}
                className="bg-indigo-600 text-white px-4 py-1.5 rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-50 hover:bg-indigo-700"
                data-testid="qa-send-btn">
                <Send className="w-4 h-4" /> {loading ? "Envoi…" : "Envoyer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
